import type { EraDriver, DriverCallback } from '../driver.type';
import { Result, ok } from 'neverthrow';
import type { Ty } from '../ty';
import type { AST } from '../ast';
import type { SQL } from '../sql';
import { Relation } from '../builder';
import { evalRelIR } from './eval-rel-ir';
import { DB, EMPTY_DB, DLL } from './eval-ddl';

export class ReferenceDB implements EraDriver {
  // TODO make this not required
  dialect: SQL.SqlDialect = null as any;

  sometimesLiesAboutWriteSuccess: boolean = true;
  supportsTransactionalDdl: boolean = true;
  defaultSqlGenOpts: SQL.SqlGenOpts;
  writeSchema: string;
  private db: DB;

  constructor(params?: { readonly writeSchema?: string; readonly db?: DB }) {
    this.defaultSqlGenOpts = {};
    this.writeSchema = params?.writeSchema ?? '@@write-schema';
    this.db = params?.db ?? EMPTY_DB;
  }

  addOnStartCallback(_callback: DriverCallback): EraDriver {
    throw new Error('Method not implemented.');
  }
  addOnEndCallback(_callback: DriverCallback): EraDriver {
    throw new Error('Method not implemented.');
  }

  async execute(
    relation: Relation,
    _opts?: SQL.SqlGenOpts | undefined
  ): Promise<Record<string, Ty.Scalar>[]> {
    return [...evalRelIR(relation.ir(), { db: this.db })];
  }

  async *query(
    relation: Relation,
    options?: { chunk?: number | undefined } & SQL.SqlGenOpts
  ): AsyncIterable<Record<string, Ty.Scalar>> {
    for await (const batch of this.queryBatch(relation, {
      chunk: options?.chunk ?? 1000,
      ...options,
    })) {
      for (const row of batch) {
        yield row;
      }
    }
  }

  async *queryBatch(
    relation: Relation,
    options: { chunk: number }
  ): AsyncIterable<Record<string, Ty.Scalar>[]> {
    const { chunk = 1000 } = options ?? {};
    const results = [...evalRelIR(relation.ir(), { db: this.db })];

    for (let i = 0; i <= Math.floor(results.length / chunk); i++) {
      const batch = results.slice(i * chunk, (i + 1) * chunk);
      if (batch.length > 0) {
        yield batch;
      }
    }
  }

  async createOrReplaceView(
    ref: { name: string; schema: string },
    relation: Relation
  ): Promise<Result<'ok', { cantParameterize: readonly Ty.Scalar[] }>> {
    this.db = DLL.createOrReplaceView(ref, relation, this.db);
    return ok('ok');
  }

  async insertInto(
    table: AST.TableDescription,
    contents: Ty.Row[] | Relation
  ): Promise<void> {
    this.db = DLL.insertInto(table, contents, this.db);
  }

  dropView(table: AST.TableIdentifier): Promise<void> {
    return this.dropTable(table);
  }

  async dropTable(table: AST.TableIdentifier): Promise<void> {
    this.db = DLL.dropTable(table, this.db);
  }

  async truncate(table: AST.TableIdentifier): Promise<void> {
    this.db = DLL.truncate(table, this.db);
  }

  async createTableFrom(
    ref: { name: string; schema: string },
    source: Relation
  ): Promise<AST.TableDescription> {
    const data = Relation.wrap(source);
    const description: AST.TableDescription = {
      ...ref,
      attributes: data.attributes,
    };
    const withTable = DLL.createTable(description, this.db);
    this.db = DLL.insertInto(description, data, withTable);
    return description;
  }

  async createTable(
    tableDescription: AST.TableDescription
  ): Promise<Result<'ok', { errorType: 'UnsupportedTransactionalDDL' }>> {
    this.db = DLL.createTable(tableDescription, this.db);
    return ok('ok');
  }

  rawQuery(
    _params: SQL.CompiledQuery & {
      attributes: { readonly [name: string]: Ty.ExtendedAttributeType } | null;
      options?: { chunk?: number | undefined } | undefined;
    }
  ): AsyncIterable<Record<string, Ty.Scalar>> {
    throw new Error('Method not implemented.');
  }

  rawExecute(
    _params: SQL.CompiledQuery & {
      attributes: { readonly [name: string]: Ty.ExtendedAttributeType } | null;
    }
  ): Promise<Record<string, Ty.Scalar>[]> {
    throw new Error('Method not implemented.');
  }

  rawQueryBatch(
    _params: SQL.CompiledQuery & {
      attributes: { readonly [name: string]: Ty.ExtendedAttributeType } | null;
      options: { chunk: number };
    }
  ): AsyncIterable<Record<string, Ty.Scalar>[]> {
    throw new Error('Method not implemented.');
  }

  async startTransaction(): Promise<void> {
    'TODO';
  }

  async endTransaction(
    _commitOrRollback: 'COMMIT' | 'ROLLBACK'
  ): Promise<void> {
    'TODO';
  }
}
